import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Save, X, EyeOff, MapPin, Phone, Mail, Globe, Clock, FileText, ArrowLeft, Settings, Tag } from 'lucide-react';
import { recommendations as recommendationsApi } from '../utils/api';

const RecommendationsManager = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    address: '',
    phone: '',
    website: '',
    email: '',
    hours: '',
    notes: '',
    isVisibleToClients: true,
    displayOrder: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recsRes, catsRes] = await Promise.all([
        recommendationsApi.getAll(),
        recommendationsApi.getCategories(),
      ]);
      setRecommendations(recsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Load recommendations error:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await recommendationsApi.getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error('Load categories error:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryLabel.trim()) return;
    try {
      await recommendationsApi.createCategory({ label: newCategoryLabel.trim() });
      setNewCategoryLabel('');
      loadCategories();
      toast.success('Category added');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Existing recommendations using it will keep their current category value.')) return;
    try {
      await recommendationsApi.deleteCategory(id);
      loadCategories();
      toast.success('Category deleted');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      name: '',
      category: categories.length > 0 ? categories[0].value : '',
      description: '',
      address: '',
      phone: '',
      website: '',
      email: '',
      hours: '',
      notes: '',
      isVisibleToClients: true,
      displayOrder: 0,
    });
  };

  const handleEdit = (recommendation) => {
    setEditingId(recommendation.id);
    setFormData({
      name: recommendation.business_name || recommendation.name,
      category: recommendation.category,
      description: recommendation.description || '',
      address: recommendation.address || '',
      phone: recommendation.phone || '',
      website: recommendation.website || '',
      email: recommendation.email || '',
      hours: recommendation.hours || '',
      notes: recommendation.notes || '',
      isVisibleToClients: recommendation.is_visible_to_clients,
      displayOrder: recommendation.display_order,
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({
      name: '',
      category: categories.length > 0 ? categories[0].value : '',
      description: '',
      address: '',
      phone: '',
      website: '',
      email: '',
      hours: '',
      notes: '',
      isVisibleToClients: true,
      displayOrder: 0,
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (isCreating) {
        await recommendationsApi.create(formData);
        toast.success('Recommendation created successfully');
      } else {
        await recommendationsApi.update(editingId, formData);
        toast.success('Recommendation updated successfully');
      }

      handleCancel();
      loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.error || 'Failed to save recommendation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recommendation?')) {
      return;
    }

    try {
      await recommendationsApi.delete(id);
      toast.success('Recommendation deleted successfully');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete recommendation');
    }
  };

  const renderForm = () => (
    <div className="card mb-6 bg-blue-50 border-2 border-blue-200">
      <h3 className="text-xl font-bold mb-4">
        {isCreating ? 'Create New Recommendation' : 'Edit Recommendation'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="input"
            placeholder="Business name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="input"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
            <option value="other">Other</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="input"
            rows="3"
            placeholder="Brief description of the business"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className="input"
            placeholder="123 Main St, City, State ZIP"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="input"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="input"
            placeholder="contact@business.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            className="input"
            placeholder="https://www.business.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hours
          </label>
          <input
            type="text"
            name="hours"
            value={formData.hours}
            onChange={handleInputChange}
            className="input"
            placeholder="Mon-Fri 9am-5pm"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            className="input"
            rows="2"
            placeholder="Additional information or special notes"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Order
          </label>
          <input
            type="number"
            name="displayOrder"
            value={formData.displayOrder}
            onChange={handleInputChange}
            className="input"
            min="0"
          />
        </div>

        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="isVisibleToClients"
              checked={formData.isVisibleToClients}
              onChange={handleInputChange}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Visible to clients
            </span>
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button onClick={handleCancel} className="btn btn-secondary flex items-center space-x-2">
          <X size={18} />
          <span>Cancel</span>
        </button>
        <button onClick={handleSave} className="btn btn-primary flex items-center space-x-2">
          <Save size={18} />
          <span>Save</span>
        </button>
      </div>
    </div>
  );

  const renderRecommendationCard = (rec) => (
    <div key={rec.id} className="card hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-xl font-bold text-gray-900">{rec.business_name || rec.name}</h3>
            {!rec.is_visible_to_clients && (
              <span className="flex items-center space-x-1 px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                <EyeOff size={12} />
                <span>Hidden</span>
              </span>
            )}
          </div>
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
            {categories.find(c => c.value === rec.category)?.label || rec.category}
          </span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(rec)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => handleDelete(rec.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {rec.description && (
        <p className="text-gray-700 mb-3">{rec.description}</p>
      )}

      <div className="space-y-2 text-sm">
        {rec.address && (
          <div className="flex items-start space-x-2 text-gray-600">
            <MapPin size={16} className="mt-0.5 flex-shrink-0" />
            <span>{rec.address}</span>
          </div>
        )}
        {rec.phone && (
          <div className="flex items-center space-x-2 text-gray-600">
            <Phone size={16} />
            <a href={`tel:${rec.phone}`} className="hover:text-primary-600">{rec.phone}</a>
          </div>
        )}
        {rec.email && (
          <div className="flex items-center space-x-2 text-gray-600">
            <Mail size={16} />
            <a href={`mailto:${rec.email}`} className="hover:text-primary-600">{rec.email}</a>
          </div>
        )}
        {rec.website && (
          <div className="flex items-center space-x-2 text-gray-600">
            <Globe size={16} />
            <a href={rec.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600">
              Visit Website
            </a>
          </div>
        )}
        {rec.hours && (
          <div className="flex items-center space-x-2 text-gray-600">
            <Clock size={16} />
            <span>{rec.hours}</span>
          </div>
        )}
        {rec.notes && (
          <div className="flex items-start space-x-2 text-gray-600 mt-3 p-3 bg-gray-50 rounded">
            <FileText size={16} className="mt-0.5 flex-shrink-0" />
            <span className="italic">{rec.notes}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/admin?tab=settings')}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 mb-3"
          >
            <ArrowLeft size={20} />
            <span>Back to Settings</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Recommendations Manager</h1>
          <p className="text-gray-600 mt-1">Manage business recommendations for clients</p>
        </div>
        {!isCreating && !editingId && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCategoryManager(!showCategoryManager)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Tag size={18} />
              <span>{showCategoryManager ? 'Hide' : 'Manage'} Categories</span>
            </button>
            <button onClick={handleCreate} className="btn btn-primary flex items-center space-x-2">
              <Plus size={20} />
              <span>Add Recommendation</span>
            </button>
          </div>
        )}
      </div>

      {/* Category Manager */}
      {showCategoryManager && !isCreating && !editingId && (
        <div className="card mb-6 bg-gray-50 border-2 border-gray-200">
          <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
            <Settings size={20} />
            <span>Manage Categories</span>
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Add or remove the categories that appear in the recommendation dropdown. Customize these to match your business type.
          </p>
          <div className="flex items-center space-x-3 mb-4">
            <input
              type="text"
              value={newCategoryLabel}
              onChange={(e) => setNewCategoryLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              className="input flex-1"
              placeholder="New category name (e.g., Veterinarian, Supplier, Partner)"
            />
            <button onClick={handleAddCategory} className="btn btn-primary flex items-center space-x-2">
              <Plus size={18} />
              <span>Add</span>
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                <span className="text-sm font-medium">{cat.label}</span>
                <button
                  onClick={() => handleDeleteCategory(cat.id)}
                  className="text-red-500 hover:text-red-700"
                  title="Delete category"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-sm text-gray-500 italic">No categories yet. Add one above.</p>
            )}
          </div>
        </div>
      )}

      {(isCreating || editingId) && renderForm()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {recommendations.map(rec => renderRecommendationCard(rec))}
      </div>

      {recommendations.length === 0 && !isCreating && (
        <div className="card text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No recommendations yet</h2>
          <p className="text-gray-600 mb-6">
            Start by adding your first business recommendation
          </p>
          <button onClick={handleCreate} className="btn btn-primary inline-flex items-center space-x-2">
            <Plus size={20} />
            <span>Add First Recommendation</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default RecommendationsManager;
